import { getOrderDocumentUrl } from '@api/vendor-portal';
import useToaster from '@hooks/toaster/useToaster';
import { useCallback, useState } from 'react';

/**
 * Opening the brand's order PDF, with the button's own loading state.
 *
 * TWO STEPS RATHER THAN A PLAIN HREF because the link is minted per request and expires — an
 * anchor pointing at storage would either need a permanently public object or a URL that
 * quietly goes stale in the markup.
 *
 * A HOOK RATHER THAN A COPY IN EACH CARD. Both Home groupings offer this button, on different
 * rows; the second copy would have been the same twenty lines with its own toast wording, and
 * the first fix applied to one card and not the other is where that stops being harmless.
 *
 * `openingId` is the order being fetched rather than a boolean, because the SKU card can show
 * several of these buttons at once and only the one that was clicked should spin.
 */
export function useOrderDocument() {
    const { showToastError } = useToaster();
    const [openingId, setOpeningId] = useState<string | null>(null);

    const open = useCallback(
        async (productionOrderId: string | null) => {
            if (!productionOrderId) return;
            setOpeningId(productionOrderId);
            try {
                const { url } = await getOrderDocumentUrl(productionOrderId);
                window.open(url, '_blank', 'noopener');
            } catch (err: any) {
                showToastError({
                    title: 'Could not open the order document',
                    subtitle:
                        err?.error?.message ?? err?.message ?? 'Something went wrong.',
                });
            } finally {
                setOpeningId(null);
            }
        },
        [showToastError],
    );

    return { open, openingId };
}
